import { Suspense, use } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router";
import { LibraryScreen } from "@/app/screens/Library";
import { AddPlantScreen } from "@/app/screens/AddPlant";
import { SpeciesSearchScreen } from "@/app/screens/SpeciesSearch";
import { PlantCandidatesScreen } from "@/app/screens/PlantCandidates";
import { ScanResultScreen } from "@/app/screens/ScanResult";
import { DetailScreen } from "@/app/screens/Detail";
import { PlantEditScreen } from "@/app/screens/PlantEdit";
import { PotDetailScreen } from "@/app/screens/PotDetail";
import { PotEditScreen } from "@/app/screens/PotEdit";
import { LoginScreen } from "@/app/screens/Login";
import { SignupScreen } from "@/app/screens/Signup";
import { ResetPasswordScreen } from "@/app/screens/ResetPassword";
import { OnboardingScreen } from "@/app/screens/Onboarding";
import { DataContextProvider, usePots, useWeather } from "./lib/data-context";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { loadGardenResource } from "./lib/garden-resource";
import { needsOnboarding } from "./lib/onboarding";
import { ErrorBoundary } from "react-error-boundary";

function LoadingScreen() {
  return (
    <div className="app-frame">
      <div className="app-canvas">
        <div className="screen-host">
          <div className="screen pad" style={{ display: "grid", placeItems: "center", minHeight: 400 }}>
            <p className="muted">Loading…</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary fallback={<p>Failed to load garden</p>}>
      <Suspense fallback={<LoadingScreen />}>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Suspense>
    </ErrorBoundary>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  // Logged out: only the auth screens exist; everything else routes to login.
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/signup" element={<SignupScreen />} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Keyed by user id so switching accounts (logout → login) remounts and
  // refetches the garden rather than reusing the previous tenant's data.
  return <AuthedApp key={user.id} />;
}

function AuthedApp() {
  // loadGardenResource() returns a module-cached promise (see garden-resource.ts)
  // so it stays stable across suspense retries instead of re-fetching forever.
  const garden = use(loadGardenResource()); // suspends until resolved

  return (
    <DataContextProvider garden={garden}>
      <Routes>
        <Route path="onboarding" element={<OnboardingGate />} />
        <Route element={<RequireOnboardingComplete />}>
          <Route element={<ScreenHostLayout />}>
            <Route index element={<LibraryScreen />} />
            <Route path="add" element={<AddPlantScreen />} />
            <Route path="species-search" element={<SpeciesSearchScreen />} />
            <Route path="identify" element={<PlantCandidatesScreen />} />
            <Route path="scan-result" element={<ScanResultScreen />} />
            <Route path="plants/:id" element={<DetailScreen />} />
            <Route path="plants/:id/edit" element={<PlantEditScreen />} />
            <Route path="pots/new" element={<PotEditScreen />} />
            <Route path="pots/:id" element={<PotDetailScreen />} />
            <Route path="pots/:id/edit" element={<PotEditScreen />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Routes>
    </DataContextProvider>
  );
}

/** Redirects new users to onboarding until they finish or skip. */
function RequireOnboardingComplete() {
  const { user } = useAuth();
  const { location } = useWeather();
  const { pots } = usePots();

  if (user && needsOnboarding(user.id, location, pots)) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}

/** Onboarding route — only shown while setup is incomplete. */
function OnboardingGate() {
  const { user } = useAuth();
  const { location } = useWeather();
  const { pots } = usePots();

  if (!user) return <Navigate to="/login" replace />;
  if (!needsOnboarding(user.id, location, pots)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="app-frame">
      <div className="app-canvas">
        <div className="screen-host">
          <OnboardingScreen />
        </div>
      </div>
    </div>
  );
}

function ScreenHostLayout() {
  return (
    <div className="app-frame">
      <div className="app-canvas">
        <div className="screen-host">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
